import { io, Socket } from "socket.io-client";
import type {
  ChatMessage,
  ConnectedPayload,
  SessionCreatedPayload,
  SessionModeChangedPayload,
  SessionClosedPayload,
  ErrorPayload,
  AssistantStreamStartPayload,
  AssistantStreamChunkPayload,
  AssistantStreamEndPayload,
} from "shared";

// Event names
const SERVER_EVENTS = {
  CONNECTED: "connected",
  MESSAGE: "message",
  SESSION_CREATED: "session_created",
  SESSION_MODE_CHANGED: "session_mode_changed",
  SESSION_CLOSED: "session_closed",
  ERROR: "error",
  // Streaming events
  ASSISTANT_STREAM_START: "assistant_stream_start",
  ASSISTANT_STREAM_CHUNK: "assistant_stream_chunk",
  ASSISTANT_STREAM_END: "assistant_stream_end",
} as const;

const CLIENT_EVENTS = {
  MESSAGE: "message",
  CHANGE_MODE: "change_mode",
  CLOSE_SESSION: "close_session",
} as const;

/**
 * Socket event callbacks
 */
export interface SocketCallbacks {
  onConnected?: (payload: ConnectedPayload) => void;
  onMessage?: (message: ChatMessage) => void;
  onSessionCreated?: (payload: SessionCreatedPayload) => void;
  onSessionModeChanged?: (payload: SessionModeChangedPayload) => void;
  onSessionClosed?: (payload: SessionClosedPayload) => void;
  onError?: (error: ErrorPayload) => void;
  onDisconnect?: (reason: string) => void;
  onReconnect?: () => void;
  // Streaming callbacks
  onStreamStart?: (payload: AssistantStreamStartPayload) => void;
  onStreamChunk?: (payload: AssistantStreamChunkPayload) => void;
  onStreamEnd?: (payload: AssistantStreamEndPayload) => void;
}

/**
 * Create and configure socket connection
 */
export function createSocket(
  serverUrl: string,
  token: string,
  callbacks: SocketCallbacks = {},
): Socket {
  const socket = io(serverUrl, {
    auth: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  // Connection events
  socket.on("connect", () => {
    console.log("[Socket] Connected:", socket.id);
  });

  socket.on("disconnect", (reason) => {
    console.log("[Socket] Disconnected:", reason);
    callbacks.onDisconnect?.(reason);
  });

  socket.on("connect_error", (error) => {
    console.error("[Socket] Connection error:", error.message);
  });

  socket.io.on("reconnect", () => {
    console.log("[Socket] Reconnected");
    callbacks.onReconnect?.();
  });

  // Server events
  socket.on(SERVER_EVENTS.CONNECTED, (payload: ConnectedPayload) => {
    console.log("[Socket] Received connected event:", payload);
    callbacks.onConnected?.(payload);
  });

  socket.on(SERVER_EVENTS.MESSAGE, (message: ChatMessage) => {
    console.log("[Socket] Received message:", message);
    callbacks.onMessage?.(message);
  });

  socket.on(SERVER_EVENTS.SESSION_CREATED, (payload: SessionCreatedPayload) => {
    console.log("[Socket] Session created:", payload);
    callbacks.onSessionCreated?.(payload);
  });

  socket.on(
    SERVER_EVENTS.SESSION_MODE_CHANGED,
    (payload: SessionModeChangedPayload) => {
      console.log("[Socket] Session mode changed:", payload);
      callbacks.onSessionModeChanged?.(payload);
    },
  );

  socket.on(SERVER_EVENTS.SESSION_CLOSED, (payload: SessionClosedPayload) => {
    console.log("[Socket] Session closed:", payload);
    callbacks.onSessionClosed?.(payload);
  });

  socket.on(SERVER_EVENTS.ERROR, (error: ErrorPayload) => {
    console.error("[Socket] Error:", error);
    callbacks.onError?.(error);
  });

  // Streaming events
  socket.on(
    SERVER_EVENTS.ASSISTANT_STREAM_START,
    (payload: AssistantStreamStartPayload) => {
      console.log("[Socket] Stream start:", payload.message_id);
      callbacks.onStreamStart?.(payload);
    },
  );

  socket.on(
    SERVER_EVENTS.ASSISTANT_STREAM_CHUNK,
    (payload: AssistantStreamChunkPayload) => {
      // Log less frequently to avoid console spam
      callbacks.onStreamChunk?.(payload);
    },
  );

  socket.on(
    SERVER_EVENTS.ASSISTANT_STREAM_END,
    (payload: AssistantStreamEndPayload) => {
      console.log("[Socket] Stream end:", payload.message_id);
      callbacks.onStreamEnd?.(payload);
    },
  );

  return socket;
}

/**
 * Send a message through the socket
 */
export function sendMessage(
  socket: Socket,
  content: string,
  type: "USER_MESSAGE" | "ADMIN_MESSAGE" = "USER_MESSAGE",
  metadata?: Record<string, unknown>,
): void {
  socket.emit(CLIENT_EVENTS.MESSAGE, {
    content,
    type,
    metadata,
  });
}

/**
 * Disconnect socket
 */
export function disconnectSocket(socket: Socket): void {
  socket.disconnect();
}
